import {
  AuthError,
  FetchUserAttributesOutput,
  JWT,
  fetchAuthSession,
  fetchUserAttributes,
} from "aws-amplify/auth";

import { queryClient } from "@/lib/react-query";
import { User } from "@/types/user";
import { UseQueryOptions, useQuery } from "@tanstack/react-query";

export const getAuthUser = async (): Promise<
  User & { payload: JWT["payload"]; token: string }
> => {
  const [session, user] = await Promise.all([
    fetchAuthSession(),
    fetchUserAttributes(),
  ]);

  if (!session?.tokens?.accessToken) {
    throw new Error("No access token");
  }
  // remove custom attributes from user
  const reducedUser = Object.fromEntries(
    Object.entries(user).filter(([key]) => !key.startsWith("custom:")),
  );
  return Object.assign({}, reducedUser, {
    avatarUrl: user["custom:avatarUrl"] ?? null,
    isMarketing: user["custom:isMarketing"] === "true",
    organisations: user["custom:organisations"] ?? "",
    payload: session.tokens.accessToken.payload,
    token: session.tokens.accessToken.toString(),
  });
};

export function setAuthUser(
  user: FetchUserAttributesOutput & {
    payload: JWT["payload"];
    token: string;
  },
) {
  queryClient.setQueryData(["user"], user);
}

export function useGetAuthUser(
  queryOptions?: Partial<
    UseQueryOptions<
      User & { payload: JWT["payload"]; token: string },
      AuthError
    >
  >,
) {
  return useQuery({
    queryKey: ["user"],
    queryFn: () => getAuthUser(),
    // because of this, we need to clear the user query when the user logs out
    staleTime: Infinity,
    retry: false,
    // if the user is not authenticated, the query will not be refetched
    retryOnMount: false,
    initialData: undefined,
    ...queryOptions,
  });
}
